import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './LoginPage.css';
import './LoginHelpPage.css';
import './ResetPasswordPage.css';
import zynityLogo from '../../assets/images/zynity_logo.png';
import { resetPasswordWithToken, validatePasswordResetToken } from '../../api/auth/passwordReset';

type FieldErrors = {
  newPassword?: string[];
  newPasswordRepeat?: string[];
};

const SECURITY_CHECK_ERROR = 'Security check could not be validated. Please try again or contact support.';
const PASSWORD_UPDATED_MESSAGE = 'Your password has been successfully updated. Proceed to Log-in.';

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sc = searchParams.get('sc') || '';
  const tsParam = searchParams.get('ts');
  const ts = tsParam ? Number(tsParam) : NaN;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({ newPassword: '', newPasswordRepeat: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const menuItems = useMemo(
    () => [
      { label: 'Public Site', href: 'https://zynity.com/' },
      { label: 'Help Request', href: '/help-request' },
      { label: 'Login', href: '/site/login' },
      { label: 'Sign Up', href: '/people/register' },
    ],
    [],
  );

  const toggleMenu = () => setIsMenuOpen((open) => !open);
  const handleLinkClick = () => setIsMenuOpen(false);

  useEffect(() => {
    setValidationMessage(null);
    setValidationError(null);
    setSubmitSuccess(null);
    setSubmitError(null);
    setFieldErrors({});

    if (!sc || Number.isNaN(ts)) {
      setValidationError(SECURITY_CHECK_ERROR);
      return;
    }

    setIsValidating(true);
    validatePasswordResetToken({ sc, ts })
      .then((data) => {
        setValidationMessage(data?.message || 'Security check validated.');
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message || SECURITY_CHECK_ERROR;
        setValidationError(message);
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [sc, ts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    setFieldErrors({});

    if (!sc || Number.isNaN(ts) || Boolean(validationError)) {
      setSubmitError(validationError || SECURITY_CHECK_ERROR);
      return;
    }

    setIsSubmitting(true);
    resetPasswordWithToken({
      sc,
      ts,
      newPassword: formValues.newPassword,
      newPasswordRepeat: formValues.newPasswordRepeat,
    })
      .then((data) => {
        setSubmitSuccess(data?.message || PASSWORD_UPDATED_MESSAGE);
      })
      .catch((err: any) => {
        if (err?.response?.status === 422) {
          setFieldErrors(err?.response?.data?.errors || {});
          return;
        }

        const message = err?.response?.data?.message || SECURITY_CHECK_ERROR;
        setSubmitError(message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const canSubmit =
    !isValidating &&
    !validationError &&
    Boolean(formValues.newPassword) &&
    Boolean(formValues.newPasswordRepeat) &&
    !isSubmitting;

  return (
    <div className="login-page login-help-page reset-password-page">
      <header className="login-navbar">
        <a className="navbar-logo" href="https://apps.zynity.com" aria-label="Zynity home">
          <img src={zynityLogo} alt="Zynity logo" />
        </a>
        <div className="navbar-menu">
          <button
            type="button"
            className="menu-button"
            aria-haspopup="true"
            aria-expanded={isMenuOpen}
            onClick={toggleMenu}
          >
            <span className="menu-icon" aria-hidden="true">
              &#9776;
            </span>
          </button>
          {isMenuOpen && (
            <div className="menu-dropdown">
              {menuItems.map((item) => (
                <a key={item.label} className="menu-item" href={item.href} onClick={handleLinkClick}>
                  {item.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="login-help-content reset-password-content">
        <h1 className="login-help-title">Reset Password</h1>
        <p className="login-help-text">Create a new password to regain access to your account.</p>

        {validationMessage && (
          <div className="login-help-alert login-help-alert--success" role="status">
            {validationMessage}
          </div>
        )}
        {validationError && (
          <div className="login-help-alert login-help-alert--error" role="alert">
            {validationError}
          </div>
        )}
        {submitError && (
          <div className="login-help-alert login-help-alert--error" role="alert">
            {submitError}
          </div>
        )}
        {submitSuccess && (
          <div className="login-help-alert login-help-alert--success" role="status">
            {submitSuccess}
          </div>
        )}

        <form className="reset-password-form" onSubmit={handleSubmit}>
          <div className="reset-password-card">
            <label className="reset-password-label" htmlFor="newPassword">
              New Password
            </label>
            <input
              className="login-help-input reset-password-input"
              id="newPassword"
              name="newPassword"
              type="password"
              placeholder="Enter new password"
              value={formValues.newPassword}
              onChange={handleInputChange}
              autoComplete="new-password"
              disabled={Boolean(validationError)}
            />
            {fieldErrors?.newPassword && (
              <div className="reset-password-error" role="alert">
                {fieldErrors.newPassword.join(' ')}
              </div>
            )}

            <label className="reset-password-label" htmlFor="newPasswordRepeat">
              Confirm New Password
            </label>
            <input
              className="login-help-input reset-password-input"
              id="newPasswordRepeat"
              name="newPasswordRepeat"
              type="password"
              placeholder="Re-enter new password"
              value={formValues.newPasswordRepeat}
              onChange={handleInputChange}
              autoComplete="new-password"
              disabled={Boolean(validationError)}
            />
            {fieldErrors?.newPasswordRepeat && (
              <div className="reset-password-error" role="alert">
                {fieldErrors.newPasswordRepeat.join(' ')}
              </div>
            )}

            <p className="reset-password-hint">
              Password must be 6-40 characters and can include letters, numbers, or !@#$%^*.
            </p>

            <button className="login-help-button" type="submit" disabled={!canSubmit}>
              {isSubmitting ? 'UPDATING...' : 'UPDATE PASSWORD'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};
